import React, { useContext, useEffect, useState } from 'react';
import {
	Filters,
	GlobalAlert,
	Header,
	Page,
	PageAuth,
	PageContainer,
	PageHeader,
	PERMISSIONS,
	Sidebar,
	SideMenu,
} from '../..';
import { CASINO_GAME_PROVIDERS_SEARCH_TYPES, gamesPages, useStore } from '../../../common';
import { AuthContext } from '../../../logic';
import { gameProvidersListMap } from './GameProviders.const';
import { PropertyType } from 'esa-ui-library/lib/types';
import styles from '../GameProviders/GameProviders.module.scss';
import { getRows } from './RowProvider';
import { Button, Modal, Pagination, PlusLight, Table } from 'esa-ui-library';
import { SAVE } from '../GamblingReport/ReportTemplates/ReportTemplates.const';
import { CreateGameProviderForm } from './components/CreateGameProviderForm';
import { AssignPartnerForm } from './components/AssignPartnerForm';
import { ShowPartners } from './components/ShowPartners';
import { SearchFilter } from '../../components/Filters/components';
import { filterSearchOptionsByEnv } from '../Games/Games.helper';

const GameProviders = (): JSX.Element => {
	const [state, dispatch] = useStore();
	const [loading, setLoading] = useState(false);
	const {
		gameProviders,
		rows,
		bulkRows,
		columns,
		pagesTotal,
		currentPage,
		gameProvidersLoading,
		perPage = '10',
		selectedGameProvider,
		gameProviderForm,
		gameProviderPartnerForm,
		activePartners,
	} = state;
	const { user, token } = useContext(AuthContext);
	const [editModal, setEditModal] = useState(false);
	const [assignPartnerModal, setAssignPartnerModal] = useState(false);
	const [showPartnersModal, setShowPartnersModal] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);
	const filteredGameProvidersFields = gameProvidersListMap;

	const setColumnsConfig = (columns: PropertyType[]) =>
		columns.map((c) => {
			return {
				...c,
				cellClassName: c.position ? styles[c.position] : '',
			};
		});

	const addBulkAll = () => {
		return [
			// <Checkbox
			// 	disabled
			// 	key={uuid()}
			// 	checked={allRowsSelected}
			// 	partial={!!bulkRows.length && !allRowsSelected}
			// 	onChange={onBulkAllChange}
			// 	dataId='bulk-check'
			// />,
			...setColumnsConfig(filteredGameProvidersFields),
			'',
		];
	};

	const onAction = (type: string, gameProvider: PropertyType) => {
		switch (type) {
			case 'edit':
				dispatch('SELECT_GAME_PROVIDER', { gameProvider });
				setEditModal(true);
				break;
			case 'showPartners':
				dispatch('GET_PROVIDER', { token, gameProvider });
				dispatch('SELECT_GAME_PROVIDER', { gameProvider });
				setShowPartnersModal(true);
				break;
			case 'assignPartner':
				dispatch('SELECT_GAME_PROVIDER', { gameProvider });
				dispatch('GET_PARTNERS', { token, gameProvider });
				setAssignPartnerModal(true);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		dispatch('LOADING', false);
		dispatch('GAME_PROVIDERS_INIT', { token, currentPage, perPage });
	}, []);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	useEffect(() => {
		dispatch('UPDATE_ROWS', getRows(gameProviders, columns, onAction));
	}, [gameProviders, columns]);

	useEffect(() => {
		dispatch('UPDATE_COLUMNS', addBulkAll());
	}, [gameProviders, bulkRows]);

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	const onAddGameProvider = () => {
		setEditModal(true);
	};

	const onCloseCreateModal = () => {
		setEditModal(false);
		dispatch('SELECT_GAME_PROVIDER', { game: null });
		dispatch('FORM_RESET');
		dispatch('MESSAGE_INVALIDATE');
	};

	const onSubmit = async () => {
		setLoading(true);
		await dispatch('GAME_PROVIDER_ADD', { token, gameProviderForm });
		setEditModal(false);
		dispatch('FORM_RESET');
		setLoading(false);
	};

	const onAssignPartnersSubmit = async () => {
		setLoading(true);
		try {
			await dispatch('PROVIDER_PARTNER_ASSIGN', {
				token,
				form: gameProviderPartnerForm,
				partner: activePartners,
				provider: selectedGameProvider,
			});
			dispatch('FORM_ASSIGN_PARTNER_RESET');
			dispatch('GLOBAL_SUCCESS', ['Assign provider successfully']);
			setAssignPartnerModal(false);
		} catch (error: any) {
			if (error?.response?.status === 400) {
				dispatch('GLOBAL_ERROR', ['Assign provider to partner failed']);
			} else {
				dispatch('GLOBAL_ERROR', ['Assign provider to partner failed']);
			}
		} finally {
			setLoading(false);
			setAssignPartnerModal(false);
			dispatch('FORM_ASSIGN_PARTNER_RESET');
		}
	};

	const onCloseAssignPartner = () => {
		setAssignPartnerModal(false);
		dispatch('SELECT_GAME', { game: null });
		dispatch('FORM_ASSIGN_PARTNER_RESET');
	};

	const onCloseShowPartners = () => {
		setShowPartnersModal(false);
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth onlyAdmin permission={PERMISSIONS.canListGames}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={gamesPages} title='Game Providers' />
					</Sidebar>
					<Page>
						<PageHeader title='Casino Game Providers'>
							{user?.permissions?.isSuperAdmin ? (
								<Button dataId='create-user' primary small onClick={onAddGameProvider}>
									<PlusLight className={styles.AddUserIcon} />
									Create Game Provider
								</Button>
							) : null}
						</PageHeader>
						<section className={styles.PageContent}>
							<Filters>
								<div style={{ marginLeft: 'auto' }}>
									<SearchFilter searchTypes={filterSearchOptionsByEnv(CASINO_GAME_PROVIDERS_SEARCH_TYPES)} />
								</div>
							</Filters>
							{(!!gameProviders?.length || gameProvidersLoading) && (
								<>
									<Table
										titles={columns}
										data={rows}
										className={styles.GameProvidersTable}
										rowClassName={styles.GameProvidersRow}
										isLoading={gameProvidersLoading}
									/>
									<Pagination
										total={Math.ceil(pagesTotal / +perPage)}
										current={currentPage}
										onChange={onPageChange}
										perPage={perPage}
										showPerPage
										onPerPage={onPerPageChange}
									/>
								</>
							)}
							{editModal && (
								<Modal
									title={selectedGameProvider ? 'Edit Game Provider' : 'Create Game Provider'}
									submitLabel={SAVE}
									primary
									onCancel={onCloseCreateModal}
									onSubmit={onSubmit}
									loading={!loading && !isFormValid}
								>
									<CreateGameProviderForm isEdit={!!selectedGameProvider} onValidityChange={setIsFormValid} />
								</Modal>
							)}
							{assignPartnerModal && (
								<Modal
									title={'Assign partners'}
									submitLabel={SAVE}
									primary
									onCancel={onCloseAssignPartner}
									onSubmit={onAssignPartnersSubmit}
									loading={!isFormValid}
								>
									<AssignPartnerForm onValidityChange={setIsFormValid} />
								</Modal>
							)}
							{showPartnersModal && (
								<Modal title={'Show provider partners'} primary onCancel={onCloseShowPartners} confirm={true}>
									<ShowPartners />
								</Modal>
							)}
						</section>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
		</>
	);
};

export { GameProviders };
